import React, { memo } from 'react'
import PropTypes from 'prop-types'
import { Box, Slide, Snackbar } from '@material-ui/core'
import MuiAlert from '@material-ui/lab/Alert'
import QUESTION_TYPE, { ALIGN_REL } from '@v4/constants/quiz'
import clsx from 'clsx'
import EditableHTML from '@v4/components/common/EditableHTML'
import PageMetaData from '@v4/utils/data/PageMetaData'
import Captcha from '@v4/components/common/Captcha'
import SortHandler from '@v4/components/common/SortHandler'
import { MEDIA_POSITION } from '@v4/components/sections/Question/Question.const'
import WhiteThemeProvider from '@v4/components/sections/Question/Components/WhiteThemeProvider'
import QuestionTitle from '../QuestionTitle'
import { useAnswerType } from './AnswerTypes.hooks'

const AnswerType = props => {
	const [
		{
			classes,
			withBgImage,
			withShadow,
			formRef,
			tokens,
			type,
			isOpen,
			message,
			id,
			isLoading,
			ActiveType,
			align,
			useOldCheckboxes,
			target,
			action,
			instruction,
			media,
			textSize,
			showNoQuestionMessage,
			showAnswerType,
			answers,
			titlePlaceholder,
			resultKey,
			captchaRef,
			instructionDirection,
			mediaPosition,
			showMedia,
			invertColors,
		},
		{ handleClose, handleSubmit },
	] = useAnswerType(props)

	const mediaComponent = media && <div className={classes.media}>{media}</div>

	return (
		<Box
			className={clsx(type, 'question-item')}
			component="form"
			action={action}
			method="POST"
			target={target}
			ref={formRef}
		>
			{resultKey === 'no-sign-up' && <Captcha ref={captchaRef} />}
			{tokens.csrf && <input type="hidden" name="_token" value={tokens.csrf} />}
			{tokens.sasid && <input type="hidden" name="SASID" value={tokens.sasid} />}
			{!!id && (
				<>
					{mediaPosition === MEDIA_POSITION.ABOVE_QUESTION && mediaComponent}
					<SortHandler direction={instructionDirection}>
						<WhiteThemeProvider>
							<QuestionTitle
								className={clsx(classes.title, 'question-item-text')}
								questionId={id}
								align={ALIGN_REL[align]}
								shadow={withShadow}
								textSize={textSize}
								placeholder={titlePlaceholder}
							/>
						</WhiteThemeProvider>
						{instruction && <div className={classes.instruction}>{instruction}</div>}
					</SortHandler>
					{mediaPosition === MEDIA_POSITION.BELOW_QUESTION && mediaComponent}

					<Box my={3.5} color="text.secondary" className="question-answer">
						{showAnswerType && (
							<ActiveType
								key={id}
								questionId={id}
								type={type}
								justify={align}
								pending={isLoading}
								onClick={handleSubmit}
								shadow={withShadow}
								withBgImage={withBgImage}
								useOldCheckboxes={useOldCheckboxes}
								answers={answers}
								mediaPosition={mediaPosition}
								showMedia={showMedia}
								invertColors={invertColors}
							/>
						)}
						<div className={classes.errorWrap}>
							<Snackbar
								anchorOrigin={{
									vertical: 'bottom',
									horizontal:
										align === PageMetaData.ALIGNMENT.CENTER
											? PageMetaData.ALIGNMENT.CENTER
											: PageMetaData.ALIGNMENT.LEFT,
								}}
								className={clsx(classes.error, 'question-error')}
								classes={{
									anchorOriginBottomLeft: classes.anchorOriginBottomLeft,
								}}
								open={isOpen}
								autoHideDuration={6000}
								TransitionComponent={Slide}
								onClose={handleClose}
							>
								<MuiAlert severity="error" onClose={handleClose}>
									{message}
								</MuiAlert>
							</Snackbar>
						</div>
					</Box>
				</>
			)}
			{!id && showNoQuestionMessage && (
				<EditableHTML
					html={`Sorry this Scorecard doesn't have any questions for you to
					answer right now, please try again later`}
					variant="h3"
					className={clsx(classes.title, 'question-item-empty')}
					shadow={withShadow}
				/>
			)}
		</Box>
	)
}

AnswerType.propTypes = {
	align: PropTypes.string,
	withShadow: PropTypes.bool,
	withBgImage: PropTypes.bool,
	useOldCheckboxes: PropTypes.bool,
	textSize: PropTypes.string,
	type: PropTypes.oneOf(Object.values(QUESTION_TYPE)),
	media: PropTypes.node,
	instructionPosition: PropTypes.string,
	mediaPosition: PropTypes.string,
	showMedia: PropTypes.bool,
	invertColors: PropTypes.bool,
	withOldTextSize: PropTypes.bool,
}

export default memo(AnswerType)
